import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { products, categories } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShoppingCart, Search, Filter } from 'lucide-react';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [filteredProducts, setFilteredProducts] = useState(products);

  useEffect(() => {
    let filtered = products;

    // Filter by category
    if (selectedCategory && selectedCategory !== 'all') {
      const category = categories.find(c => c.id === selectedCategory);
      if (category) {
        filtered = filtered.filter(p => p.category === category.name);
      }
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  }, [selectedCategory, searchTerm]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId !== 'all') {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Medical Equipment Catalog | Kalorama Medical"
        description="Browse our extensive selection of durable medical equipment including wheelchairs, walkers, rollators, hospital beds, bath safety products and more."
        keywords="medical equipment catalog, wheelchairs, walkers, rollators, hospital beds, bath safety, mobility aids"
      />
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Our Medical Devices
          </h1>
          <p className="text-xl text-blue-100">
            Quality equipment for better living
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-4 text-[#1e3a5f] flex items-center">
                    <Filter className="h-5 w-5 mr-2" />
                    Filters
                  </h3>
                  
                  {/* Search */}
                  <div className="mb-6">
                    <label className="text-sm font-medium mb-2 block text-gray-700">
                      Search Products
                    </label>
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                      <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                    </div>
                  </div>

                  {/* Categories */}
                  <div>
                    <label className="text-sm font-medium mb-3 block text-gray-700">
                      Categories
                    </label>
                    <div className="space-y-2">
                      <button
                        onClick={() => handleCategoryChange('all')}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-colors duration-200 ${
                          selectedCategory === 'all'
                            ? 'bg-[#1e3a5f] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        All Products
                      </button>
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => handleCategoryChange(category.id)}
                          className={`w-full text-left px-4 py-2 rounded-lg transition-colors duration-200 ${
                            selectedCategory === category.id
                              ? 'bg-[#1e3a5f] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            <div className="mb-6">
              <p className="text-gray-600">
                Showing {filteredProducts.length} products
              </p>
            </div>

            {filteredProducts.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map((product) => (
                  <Card key={product.id} className="group hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="mb-3">
                        <span className="text-xs font-medium text-[#7bb5d4] bg-blue-50 px-3 py-1 rounded-full">
                          {product.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-[#1e3a5f] group-hover:text-[#7bb5d4] transition-colors duration-200">
                        {product.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                        {product.description}
                      </p>
                      <div className="border-t border-gray-200 pt-4 mb-4">
                        <h4 className="text-xs font-semibold text-gray-700 mb-2">Key Features:</h4>
                        <ul className="space-y-1">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="text-xs text-gray-600 flex items-start">
                              <span className="text-[#7bb5d4] mr-2">•</span>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/product/${product.id}`} className="flex-1">
                          <Button className="w-full bg-[#1e3a5f] hover:bg-[#2c5282] text-white rounded-full transition-all duration-300 text-sm">
                            View Details
                          </Button>
                        </Link>
                        <Link to="/contact" className="flex-1">
                          <Button
                            className="w-full bg-[#7bb5d4] hover:bg-[#6aa4c3] text-white rounded-full transition-all duration-300 text-sm"
                          >
                            Get Quote
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-gray-600 text-lg mb-4">No products found</p>
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                  className="bg-[#1e3a5f] hover:bg-[#2c5282] text-white rounded-full"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
